import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { useMerchant } from '@/src/context/merchant/merchant-context'
import SvgPlus from '@/src/assets/svgs/collection/plus.svg'
import SvgSearch from '@/src/assets/svgs/collection/search.svg'
import ImageFallback from '@/src/components/helpers/image-fallback/image-fallback'
import BackButton from '@/src/components/misc/back-button/back-button'
import { CustomSize } from '@/src/context/product/current-product-context'
import { ItemService } from '@/src/services/catalog/item-service'
import Items from '@/src/interfaces/catalog/items'
import LoadingHalfFlavors from './loading-half-flavors/loading-half-flavors'
import { usePromotion } from '@/src/context/promotion/promotion-context'
import { PromotionHelper } from '@/src/helpers/promotion'
import { useAppAccessToken } from '@/src/context/app-access-token/app-access-token-context'

interface Props {
    sizeId: string
    selectedItemId: string
    halfsSelected: CustomSize[] | undefined
    maxParts: number
    moveBack(): void
    getFlavors(e: CustomSize[]): void
}

export default function ChooseHalfFlavors(props: Props) {
    const { merchantSummary, localMode } = useMerchant()
    const { promotion } = usePromotion()
    const { appAccessToken } = useAppAccessToken()

    const [flavors, setFlavors] = useState<CustomSize[]>([])
    const [showInput, setShowInput] = useState<boolean | undefined>(undefined)
    const [inputValue, setInputValue] = useState<string | undefined>()
    const [halfs, setHalfs] = useState<CustomSize[]>(props.halfsSelected ?? [])
    const [loading, setLoading] = useState<boolean>(true)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

    const maxHalfsTarget = (): boolean => {
        return halfs.length === props.maxParts
    }

    const handleItemClick = (item: CustomSize) => {
        setHalfs((prevHalfs) => {
            const isAlreadySelected = prevHalfs.some((half) => half.id === item.id);
            maxHalfsTarget()
            if (isAlreadySelected)
                return prevHalfs.filter((half) => half.id !== item.id);
            else {
                item.quantity = Math.round(1 / (props.halfsSelected?.length ?? 1))
                return [...prevHalfs, item];
            }
        });
    };

    const isItemSelected = (item: CustomSize) => {
        return halfs.some((half) => half.id === item.id);
    };

    const moveBack = () => {
        setHalfs([])
        props.moveBack()
    }

    const confirmChoice = () => {
        props.getFlavors(halfs)
        props.moveBack()
    }

    const isHalfFound = (item: CustomSize) => {
        return item.name.toLowerCase().includes(inputValue?.toLowerCase() || '')
    }

    const renderPrice = (item: CustomSize) => {
        const promotionProduct = PromotionHelper.getProduct(promotion, item.id)

        if (promotionProduct)
            return <p className={`${styles.price} font-sm`}>De <s>{item?.originalPrice ? item.originalPrice.toLocalePrice() : ''}</s> por <b>{item.salePrice.toLocalePrice()}</b></p>

        return <span>{item.salePrice.toLocalePrice()}</span>
    }

    useEffect(() => {
        if (!showInput)
            setInputValue(undefined)
    }, [showInput])

    useEffect(() => {
        const getHalfFlavors = async () => {
            try {
                const params: Items.SizeSearchParams = {
                    sizeId: props.sizeId,
                    merchantId: merchantSummary?.id!,
                    sellLocal: !!localMode
                }

                const response = await ItemService.getBySizeSummary(params, appAccessToken)

                if (!response.success || !response.model)
                    throw Error(response.errorMessage)

                let responseHalfList = response.model.items

                for (const item of responseHalfList) {
                    const promotionProduct = PromotionHelper.getProduct(promotion, item.id)

                    if (promotionProduct) {
                        item.originalPrice = item.salePrice
                        item.salePrice = PromotionHelper.getFromToPrice(promotion, { salePrice: item.salePrice, id: item.id }).to
                        item.promoId = PromotionHelper.getPromotionByProductId(promotion, item.id)?.id
                        item.promoPriceType = PromotionHelper.getProduct(promotion, item.id)?.valueType
                    }
                }

                const principalPart = responseHalfList.find(c => c.id == props.selectedItemId)

                if (principalPart) {
                    const filteredList = responseHalfList.filter(c => c.id !== principalPart.id)

                    filteredList.unshift(principalPart)

                    responseHalfList = filteredList

                    if (halfs.find(c => c.id === principalPart.id) === undefined)
                        setHalfs([...halfs, principalPart as CustomSize])
                }

                setFlavors(responseHalfList as CustomSize[])
            }
            catch (e) {
                const error = e as Error
                setErrorMessage(error.message)
            }
            finally {
                setLoading(false)
                maxHalfsTarget()
            }
        }

        getHalfFlavors()
    }, [])

    if (errorMessage)
        return <p>{errorMessage}</p>

    return (
        <div className={styles.container}>
            <div className={styles.head}>
                <BackButton className={styles.backButton} onClick={() => props.moveBack()} />
                <div className={styles.headTitleArea}>
                    {!showInput && <span className={styles.headTitle}>Adicione até {props.maxParts} sabores</span>}
                    <div onClick={() => setShowInput(true)} className={`${styles.inputArea} ${showInput && styles.showInput}`}>
                        {
                            showInput &&
                            <>
                                <input
                                    autoFocus={true}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    className={styles.searchInput}
                                />
                                <SvgPlus
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        setShowInput(false);
                                    }}
                                    className={styles.svgClose}
                                />
                            </>
                        }
                        {
                            !showInput &&
                            <div className={styles.search}>
                                <SvgSearch className={styles.svgSearch} />
                            </div>
                        }
                    </div>
                </div>
            </div>
            <div className={styles.products}>
                {
                    loading
                        ?
                        <LoadingHalfFlavors />
                        :
                        flavors.length > 0 && flavors.map((item: CustomSize, index) => {
                            const imgSrc = (item.hasPhoto && merchantSummary?.showProductPhoto) ? `//${item?.smallImageUrl}` : undefined

                            return (
                                <div onClick={() => handleItemClick(item)} className={`${isHalfFound(item) ? styles.product : styles.hideProduct}
                            ${(maxHalfsTarget() && !isItemSelected(item) || index === 0) ? styles.disabledItem : ''}`} key={index}>
                                    <div className={styles.imageArea}>
                                        <ImageFallback src={imgSrc} alt='product' />
                                    </div>
                                    <div className={styles.descriptionArea}>
                                        <h5 className='font-md'>{item.name}</h5>
                                        <p className='font-sm'>{item.description}</p>
                                        {renderPrice(item)}
                                    </div>
                                    <div className={styles.chkArea}>
                                        <div className={`${styles.chk} ${isItemSelected(item) && styles.chkActive}`} />
                                    </div>
                                </div>
                            )

                        })
                }
            </div>
            <div className={styles.bottom}>
                <button className={styles.btnBottomCancel} onClick={() => moveBack()}>Cancelar</button>
                <button className={styles.btnBottomConfirm} onClick={() => confirmChoice()}>Confirmar</button>
            </div>
        </div>
    )
}